import React from "react";
import {
  Card,
  CardContent,
  CardActions,
  Box,
  Avatar,
  Typography,
  Chip,
  Stack,
  Button,
  IconButton,
  Tooltip,
  alpha,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineTwoToneIcon from "@mui/icons-material/DeleteOutlineTwoTone";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { getActiveCategories } from "./permissionCategories";

const ACCENT = "#1976d2";
const MAX_VISIBLE_CATEGORIES = 3;

const RoleCard = ({ role, pageNames, translation, onEdit, onDelete }) => {
  const categories = getActiveCategories(role, pageNames);
  const visibleCategories = categories.slice(0, MAX_VISIBLE_CATEGORIES);
  const extraCount = categories.length - visibleCategories.length;

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${alpha("#000000", 0.06)}`,
        background: "linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          boxShadow: "0 12px 32px rgba(0, 0, 0, 0.12)",
          transform: "translateY(-4px)",
          borderColor: alpha(ACCENT, 0.4),
        },
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha("#f8fafc", 0.8)} 0%, ${alpha(ACCENT, 0.08)} 100%)`,
          p: 2.5,
          borderBottom: `2px solid ${alpha(ACCENT, 0.12)}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
          <Avatar
            sx={{
              background: `linear-gradient(135deg, ${ACCENT} 0%, ${alpha(ACCENT, 0.8)} 100%)`,
              width: 48,
              height: 48,
              fontSize: "1.3rem",
              fontWeight: 700,
              boxShadow: `0 4px 12px ${alpha(ACCENT, 0.4)}`,
            }}
          >
            {role?.Name?.charAt(0) || "?"}
          </Avatar>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1.05rem",
              color: "#1e293b",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={role?.Name}
          >
            {role?.Name}
          </Typography>
        </Box>
        <Tooltip title={translation.deleteRole}>
          <IconButton size="small" color="warning" onClick={() => onDelete(role?._id)} aria-label="delete role">
            <DeleteOutlineTwoToneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", gap: 1.25 }}>
        {role?.admin ? (
          <Chip
            icon={<VerifiedUserIcon />}
            label="ADMIN — גישה מלאה"
            size="small"
            sx={{ alignSelf: "flex-start", color: ACCENT, borderColor: ACCENT }}
            variant="outlined"
          />
        ) : categories.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            אין הרשאות פעילות
          </Typography>
        ) : (
          <Stack direction="row" gap={0.75} flexWrap="wrap">
            {visibleCategories.map((cat) => (
              <Chip
                key={cat.title}
                label={`${cat.title} (${cat.count})`}
                size="small"
                sx={{ bgcolor: alpha(ACCENT, 0.08), color: ACCENT, fontWeight: 600 }}
              />
            ))}
            {extraCount > 0 && (
              <Chip label={`+${extraCount}`} size="small" variant="outlined" sx={{ color: "text.secondary" }} />
            )}
          </Stack>
        )}
        <Typography variant="caption" color="text.secondary">
          {role?.Branches?.length ? `${role.Branches.length} סניפים` : "כל הסניפים"}
        </Typography>
      </CardContent>

      <CardActions sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
        <Button
          fullWidth
          variant="contained"
          size="small"
          startIcon={<EditOutlinedIcon />}
          onClick={() => onEdit(role)}
          sx={{
            borderRadius: 2.5,
            textTransform: "none",
            fontWeight: 600,
            background: `linear-gradient(135deg, ${ACCENT} 0%, ${alpha(ACCENT, 0.85)} 100%)`,
          }}
        >
          {translation.editPermissions || "ערוך הרשאות"}
        </Button>
      </CardActions>
    </Card>
  );
};

export default RoleCard;
